// API client for the gait server.
//
// Cross-origin now: the SPA is served from Cloudflare Pages and the API lives on
// cint2 behind a tunnel, so every request carries an explicit Authorization
// header and the server has a CORS allowlist. No cookies are involved, which is
// why the API sets allow_credentials=false.

import { auth } from './firebase';
import type { AssistantMessage, AssistantReply, SessionVisualizations } from './types';
// Defaults to production rather than to an empty string. An unset variable
// would otherwise make every call a same-origin request to the Pages host,
// which 404s — a broken deploy that looks like a broken API. The hostname is
// not a secret (odi-nodi's build.gradle.kts carries the same default), and any
// environment can override it at build time.
const DEFAULT_BASE = 'https://gait.gmavrt.me';
const BASE = (import.meta.env.VITE_API_BASE_URL || DEFAULT_BASE).replace(/\/+$/, '');

/** Dispatched on a 401 so the auth context can drop the session. */
export const UNAUTHORIZED_EVENT = 'auth:unauthorized';

export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Current Firebase ID token, refreshed by the SDK when near expiry.
 *
 * Requests that start together share one token lookup rather than each asking
 * Firebase, which matters at startup when /me and the dashboard go out at once.
 *
 * Bounded: if Firebase is unreachable, `getIdToken` can hang, and hanging is the
 * worst failure mode here — the UI shows a spinner for ever with no way to tell
 * whether the network or the login is at fault. Fail fast and let the caller
 * report it.
 */
let pendingToken: Promise<string | null> | null = null;

export function idToken(): Promise<string | null> {
  // Null when the build carried no Firebase config; requests then go out
  // unauthenticated and get a clean 401 rather than throwing here.
  const user = auth?.currentUser;
  if (!user) return Promise.resolve(null);
  pendingToken ??= Promise.race([
    user.getIdToken(),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('token refresh timed out')), 6000)
    ),
  ])
    .catch(() => null)
    .finally(() => {
      pendingToken = null;
    });
  return pendingToken;
}

/** The dashboard's first request. Shared so the startup prefetch matches it exactly. */
export const RECENT_SESSIONS_PATH = '/api/sessions?page=1&page_size=5&sort=date_desc';

/**
 * GET requests currently on the wire, by path. A second caller asking for the
 * same path while one is running gets that request instead of a duplicate.
 * Entries leave when the request settles, so nothing here is ever served stale.
 */
const inflight = new Map<string, Promise<unknown>>();

/**
 * Start a GET now so a screen that asks for it later joins the running request.
 * Fire-and-forget: a failure is swallowed here and surfaces to whichever screen
 * actually awaits the path.
 */
export function prefetchApi(path: string): void {
  api(path).catch(() => {});
}

/**
 * Forget every in-flight GET. Called when the signed-in identity changes, so a
 * request made for the previous account can never be handed to the next one.
 */
export function forgetInflight(): void {
  inflight.clear();
}

export function api<T = unknown>(path: string, opts: RequestInit = {}): Promise<T> {
  if ((opts.method ?? 'GET').toUpperCase() !== 'GET') return request<T>(path, opts);
  const running = inflight.get(path);
  if (running) return running as Promise<T>;
  const fresh = request<T>(path, opts);
  inflight.set(path, fresh);
  const settle = () => {
    // Only remove our own entry: forgetInflight may have replaced it since.
    if (inflight.get(path) === fresh) inflight.delete(path);
  };
  fresh.then(settle, settle);
  return fresh;
}

async function request<T>(path: string, opts: RequestInit): Promise<T> {
  const headers = new Headers(opts.headers);
  const token = await idToken();
  if (token) headers.set('Authorization', 'Bearer ' + token);

  let res: Response;
  try {
    res = await fetch(BASE + path, { ...opts, headers });
  } catch {
    // Also what a CORS rejection looks like from here — the browser refuses to
    // tell JavaScript why. Worth saying out loud, because "network error" on a
    // freshly deployed preview URL is almost always a missing CORS origin.
    throw new ApiError(
      'Could not reach the gait server. Check that it is running and that this origin is allowed.',
      0
    );
  }

  if (res.status === 401) {
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }

  let data: unknown = null;
  if ((res.headers.get('Content-Type') || '').includes('application/json')) {
    try {
      data = await res.json();
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    throw new ApiError(errorMessage(data, res.status), res.status, data);
  }
  return data as T;
}

/**
 * FastAPI puts the message in `detail`, which is sometimes a string and
 * sometimes an object (auth uses `{reason}`). 403s from the users table carry
 * the sentence a signed-in-but-unapproved person most needs to read, so it is
 * worth digging it out rather than showing a bare status code.
 */
function errorMessage(data: unknown, status: number): string {
  if (data && typeof data === 'object') {
    const detail = (data as { detail?: unknown }).detail;
    if (typeof detail === 'string' && detail) return detail;
    if (detail && typeof detail === 'object') {
      const reason = (detail as { reason?: unknown }).reason;
      if (typeof reason === 'string') return reason.replace(/_/g, ' ');
    }
  }
  return `Request failed (${status})`;
}

/** Permanently remove one recording. Admin only; the server enforces that. */
export async function deleteSession(sessionId: string): Promise<void> {
  await api(`/api/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
}

export interface BulkDeleteResult {
  deleted: number;
  failed: number;
  results: { sessionId: string; deleted: boolean; reason?: string }[];
}

/**
 * Remove several recordings.
 *
 * Reports per-session outcomes rather than a single status, because a partial
 * failure needs to say *which* ones went — otherwise the caller is left checking
 * by hand.
 */
export function bulkDeleteSessions(sessionIds: string[]): Promise<BulkDeleteResult> {
  return api<BulkDeleteResult>('/api/sessions/bulk-delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionIds }),
  });
}

/**
 * Re-run the current pipeline on one stored recording. Answers at once; the
 * session is 'processing' until the background run finishes, and the detail
 * page polls it there.
 */
export function recomputeSession(sessionId: string): Promise<{ sessionId: string; status: string }> {
  return api(`/api/sessions/${encodeURIComponent(sessionId)}/recompute`, { method: 'POST' });
}

export interface RecomputeManyResult {
  queued: number;
  skipped: number;
  sessionIds: string[];
}

/** Re-run the pipeline on the selected recordings, or every one. Admin only. */
export function recomputeSessions(
  target: { sessionIds: string[] } | { all: true }
): Promise<RecomputeManyResult> {
  return api<RecomputeManyResult>('/api/sessions/recompute', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(target),
  });
}

export interface UploadArgs {
  left: File;
  right: File;
  /** Optional third sensor on the pelvis; the server adds pelvis metrics when present. */
  pelvis?: File | null;
  clinicalId: string;
  assessmentName: string;
  samplingRateHz: number;
  onProgress?: (fraction: number) => void;
}

/**
 * Submit a bilateral walk for analysis.
 *
 * Uses XHR rather than fetch for one reason: fetch cannot report upload
 * progress, and these are multi-megabyte files over clinic connections where a
 * silent several-second wait reads as a hung page.
 *
 * The server answers 202 as soon as the files are stored and analyses in the
 * background, so this resolves quickly even for a long walk. The returned
 * session id is what the caller watches for completion.
 */
export function uploadSession(args: UploadArgs): Promise<{ sessionId: string; status: string }> {
  const form = new FormData();
  form.append('left_csv', args.left, args.left.name);
  form.append('right_csv', args.right, args.right.name);
  if (args.pelvis) form.append('pelvis_csv', args.pelvis, args.pelvis.name);
  form.append('clinicalId', args.clinicalId);
  form.append('assessmentName', args.assessmentName);
  form.append('samplingRateHz', String(args.samplingRateHz));

  return new Promise(async (resolve, reject) => {
    const token = await idToken();
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}/api/uploads`);
    if (token) xhr.setRequestHeader('Authorization', 'Bearer ' + token);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) args.onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        /* fall through to the status-based message */
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as { sessionId: string; status: string });
        return;
      }
      if (xhr.status === 401) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
      // 422 carries the server's own message naming exactly what's wrong with
      // the submission, which is far more useful than a status code.
      reject(new ApiError(errorMessage(body, xhr.status), xhr.status, body));
    };
    xhr.onerror = () =>
      reject(new ApiError('Could not reach the gait server while uploading.', 0));
    xhr.send(form);
  });
}

/**
 * Fetch a stored file as a blob and hand it to the browser as a download.
 *
 * A plain <a href> can't be used: the endpoint needs an Authorization header,
 * and a link element cannot set one. Putting the token in the query string
 * instead would leak it into history, referrers and server logs — exactly the
 * finding (H5) recorded against gma_vrt's legacy player.
 */
/**
 * One of a session's stored files as a Blob. Files sit behind the Firebase token,
 * so an <img src> cannot fetch them directly; callers turn the blob into an object
 * URL and revoke it when done.
 */
export async function fetchFile(sessionId: string, filename: string): Promise<Blob> {
  const token = await idToken();
  const res = await fetch(
    `${BASE}/api/sessions/${encodeURIComponent(sessionId)}/files/${encodeURIComponent(filename)}`,
    { headers: token ? { Authorization: 'Bearer ' + token } : {} }
  );
  if (res.status === 401) {
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }
  if (!res.ok) throw new ApiError(`Could not download ${filename}`, res.status);
  return res.blob();
}

export function getSessionVisualizations(
  sessionId: string,
): Promise<SessionVisualizations> {
  return api<SessionVisualizations>(
    `/api/sessions/${encodeURIComponent(sessionId)}/visualizations`,
  );
}

export async function downloadFile(sessionId: string, filename: string): Promise<void> {
  const url = URL.createObjectURL(await fetchFile(sessionId, filename));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${sessionId}_${filename}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/*
 * Algorithm scripts, users and bulk download are served by the gait API's
 * `ars` branch; until that ships, these endpoints 404 against production.
 */

export interface AlgorithmScriptFile {
  id: string;
  filename: string;
  sizeBytes: number;
  sha256: string;
}

export interface AlgorithmScript {
  id: string;
  name: string;
  version: string;
  active: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  files: AlgorithmScriptFile[];
}

export function getAlgorithms(): Promise<AlgorithmScript[]> {
  return api<AlgorithmScript[]>('/api/algorithms');
}

export function createAlgorithm(name: string, version: string, files: File[]): Promise<AlgorithmScript> {
  const form = new FormData();
  form.append('name', name);
  form.append('version', version);
  for (const file of files) form.append('files', file, file.name);
  return api<AlgorithmScript>('/api/algorithms', { method: 'POST', body: form });
}

/** Only one version is active at a time; the server deactivates the others. */
export function setAlgorithmActive(algorithmId: string, active: boolean): Promise<AlgorithmScript> {
  const form = new FormData();
  form.append('active', String(active));
  return api<AlgorithmScript>(`/api/algorithms/${encodeURIComponent(algorithmId)}/active`, {
    method: 'PATCH',
    body: form,
  });
}

export type AppUserRole = 'admin' | 'clinician';
export type AppUserStatus = 'active' | 'pending' | 'suspended';

export interface AppUser {
  uid: string;
  email: string;
  displayName: string | null;
  role: AppUserRole;
  status: AppUserStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserArgs {
  email: string;
  displayName: string;
  role: AppUserRole;
  status: AppUserStatus;
  temporaryPassword: string;
}

export function getUsers(): Promise<AppUser[]> {
  return api<AppUser[]>('/api/users');
}

export function createUser(args: CreateUserArgs): Promise<AppUser> {
  return api<AppUser>('/api/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
}

/** Download the chosen recordings as one zip. Not routed through api(): the body is binary. */
export async function bulkDownloadSessions(sessionIds: string[]): Promise<void> {
  const token = await idToken();
  const res = await fetch(`${BASE}/api/sessions/bulk-download`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ sessionIds }),
  });
  if (res.status === 401) {
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }
  if (!res.ok) {
    let data: unknown = null;
    if ((res.headers.get('Content-Type') || '').includes('application/json')) {
      try {
        data = await res.json();
      } catch {
        data = null;
      }
    }
    throw new ApiError(errorMessage(data, res.status), res.status, data);
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = 'recordings.zip';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Same bytes as fetchFile; kept for SessionDetail's preview call. */
export function viewFile(sessionId: string, filename: string): Promise<Blob> {
  return fetchFile(sessionId, filename);
}

/**
 * One turn of the recordings assistant. Stateless on the server: the whole
 * conversation goes up each time, and `sessionId` says which recording the user
 * is looking at so "this recording" means something.
 */
export function assistantChat(
  messages: AssistantMessage[],
  sessionId?: string
): Promise<AssistantReply> {
  return request<AssistantReply>('/api/assistant/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, sessionId: sessionId ?? null }),
  });
}
